import { useCallback, useEffect, useRef } from "react";
import { FormSpy, useForm, useFormState } from "react-final-form";

import { useDebounce } from "@bps/hooks";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { whenPermission } from "@ui-components/withPerm.tsx";

const ONE_MINUTE = 60000;

const SubmissionFormAutosaveBase = () => {
  const form = useForm();
  const { dirty, valid } = useFormState();

  // This component saves the form when we navigate away.  In order to do that, it needs
  //  to check if the form is worth submitting (the form is dirty and valid).  Unfortunately
  //  form.getState doesn't seem to be returning the expected values for dirty and valid -
  //  possibly because the form is in the process of unmounting.  For now we are storying dirty
  //  and valid in these refs
  const dirtyRef = useRef(false);
  const validRef = useRef(true);

  useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);

  useEffect(() => {
    validRef.current = valid;
  }, [valid]);

  const autosave = useCallback(() => {
    if (dirtyRef.current && validRef.current) {
      form.submit();
    }
  }, [form]);

  const autosaveDebounce = useDebounce(autosave, ONE_MINUTE);

  useEffect(() => {
    return () => {
      autosave();
      autosaveDebounce.cancel();
    };
  }, [autosave, autosaveDebounce]);

  return (
    <FormSpy
      subscription={{ values: true, dirty: true }}
      onChange={({ dirty }) => {
        if (dirty) {
          autosaveDebounce();
        } else {
          autosaveDebounce.cancel();
        }
      }}
    />
  );
};

/**
 * A component that will autosave the form when the user navigates away
 *  or is inactive for 60 seconds - only if the form is valid
 */
export const SubmissionFormAutosave = whenPermission(
  Permission.ClinicalAutosavePrerel,
  SubmissionFormAutosaveBase
);
